import api, { normalizeApiError } from './index';
import { AxiosResponse } from 'axios';

// --- Type Definitions ---
interface User {
  id: string;
  fullName: string;
  email: string;
  // Add any other user properties returned by the 'me' endpoint
}

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface LoginResponse {
  token: string;
  userId: string;
  user: User;
}

interface VerifyLoginCodePayload {
  email: string;
  code: string;
}

interface PasswordChangePayload {
  oldPassword?: string;
  newPassword?: string;
}

interface PasswordResetPayload {
  email: string;
  token: string;
  newPassword: string;
}

// --- API Functions ---
export const register = async (userData: any): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Registration failed');
  }
};

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  try {
    const response = await api.post('/auth/login', credentials);
    const { token, userId, user } = response.data;

    // Backwards-compatible: backend now also sets an httpOnly cookie session.
    // Keep token storage as fallback until cookie-based auth is fully rolled out.
    if (token) localStorage.setItem('token', token);
    if (userId) localStorage.setItem('userId', userId);
    localStorage.setItem("userName", user?.fullName || "Guest");

    return response.data;
  } catch (error: any) {
    const normalizedError = normalizeApiError(error, 'Login failed');
    const errorMsg = normalizedError.message;
    if (
      errorMsg.toLowerCase().includes("not verified") ||
      errorMsg.toLowerCase().includes("verify your email")
    ) {
      await resendVerificationCode(credentials.email);
      localStorage.setItem("emailForLogin", credentials.email);
      throw new Error("Email not verified. We've resent the verification code.");
    }

    throw normalizedError;
  }
};

export const googleLogin = async (idToken: string): Promise<LoginResponse> => {
    try {
        const response = await api.post('/auth/google', { idToken });
        const { token, userId, user } = response.data;
        if (token) localStorage.setItem('token', token);
        if (userId) localStorage.setItem('userId', userId);
        localStorage.setItem("userName", user?.fullName || "Guest");

        return response.data;
    } catch (error: any) {
        throw normalizeApiError(error, 'Google login failed');
    }
};

export const changePassword = async (passwordData: PasswordChangePayload): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/auth/change-password', passwordData);
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Password change failed');
  }
};

export const getMe = async (): Promise<User> => {
  try {
    // Prefer cookie session (axios is configured withCredentials: true).
    // Fallback to Bearer token if present.
    const token = localStorage.getItem('token');
    const response = token
      ? await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      : await api.get('/auth/me');
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Failed to fetch user info');
  }
};

export const updateMe = async (userData: any): Promise<AxiosResponse> => {
  try {
    const token = localStorage.getItem('token');
    const response = token
      ? await api.put('/auth/me', userData, { headers: { Authorization: `Bearer ${token}` } })
      : await api.put('/auth/me', userData);
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Failed to update user info');
  }
};

export const logout = async (): Promise<AxiosResponse> => {
  try {
    const response = await api.post("/auth/logout");
    return response.data;
  } catch (error: any) {
    // Logout should be best-effort.
    return { ok: false } as any;
  }
};

export const verifyLoginCode = async ({ email, code }: VerifyLoginCodePayload): Promise<AxiosResponse> => {
  try {
    const res = await api.post("/auth/verify-signup-code", { email, code });
    localStorage.setItem("token", res.data.token);
    return res.data;
  } catch (err: any) {
    throw normalizeApiError(err, "Code verification failed");
  }
};

export const resendVerificationCode = async (email: string): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/auth/resend-verification-code', { email });
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Failed to resend verification code');
  }
};

export const requestPasswordReset = async (email: string): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/auth/request-reset', { email });
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Failed to send reset link');
  }
};

export const resetPassword = async ({ email, token, newPassword }: PasswordResetPayload): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/auth/reset-password', {
      email,
      token,
      newPassword,
    });
    return response.data;
  } catch (error: any) {
    throw normalizeApiError(error, 'Failed to reset password');
  }
};